<template>
  <div
    v-outside-click="closeNotificationCenter"
    class="notification-center-container"
  >
    <h2 class="notification-center-heading">
      {{ $t('notifications_title') }}
    </h2>
    <div v-if="isLoading" class="notification-skeleton">
      <div v-for="i in 6" :key="i" class="notification-skeleton-item">
        <div class="notification-skeleton-icon" />
        <div class="notification-skeleton-body">
          <div
            class="notification-skeleton-line notification-skeleton-line--message"
          />
          <div
            class="notification-skeleton-line notification-skeleton-line--message-short"
          />
          <div
            class="notification-skeleton-line notification-skeleton-line--timestamp"
          />
        </div>
      </div>
    </div>

    <template v-if="fetchNotificationsFailed">
      <div v-if="!isLoading" class="no-notifications">
        <BaseIcon
          icon="bell"
          :width="40"
          :height="40"
          class="no-notifications-icon"
        />
        <h4 class="no-notifications-heading">
          {{ $t('notifications_error_title') }}
        </h4>
        <p class="no-notifications-text">
          {{ $t('notifications_error_text') }}
        </p>
      </div>
    </template>

    <template v-else-if="notificationsCount === 0">
      <div v-if="!isLoading" class="no-notifications">
        <BaseIcon
          icon="bell"
          :width="40"
          :height="40"
          class="no-notifications-icon"
        />
        <h4 class="no-notifications-heading">
          {{ $t('notifications_empty_title') }}
        </h4>
        <p class="no-notifications-text">
          {{ $t('notifications_empty_text') }}
        </p>
      </div>
    </template>

    <template v-else>
      <NotificationsGroup
        :notifications="allNotifications"
        @notificationItemClick="handleNotificationClick"
      >
        <template slot="heading">
          <br />
          <!-- <h4 class="section-header">{{ $t('notifications_last_month') }}</h4> -->
        </template>
      </NotificationsGroup>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import NotificationsGroup from './NotificationsGroup';
import outsideClick from '@/directives/outside-click';
import events from '@/utils/helpers/events';
import BaseIcon from '@/components/BaseIcon.vue';
import { Sentry } from '@/plugins/sentry';

export default {
  name: 'NotificationCenterContainer',

  components: {
    NotificationsGroup,
    BaseIcon
  },
  directives: {
    outsideClick
  },
  computed: {
    ...mapGetters({
      notificationsCount: 'notificationCenter/getNotificationsCount',
      todaysNotifications: 'notificationCenter/getTodaysNotifications',
      lastWeeksNotifications: 'notificationCenter/getLastWeeksNotifications',
      lastMonthsNotifications: 'notificationCenter/getLastMonthsNotifications',
      allNotifications: 'notificationCenter/getNotifications',
      isLoading: 'notificationCenter/isLoading',
      fetchNotificationsFailed: 'notificationCenter/fetchNotificationsFailed',
      primaryPropositionIsLimited: 'myPropositions/primaryPropositionIsLimited'
    }),
    ...mapState('notificationCenter', ['fetchNotificationsFailed'])
  },

  methods: {
    ...mapActions({
      markAsRead: 'notificationCenter/markAsRead',
      closeNotificationCenter: 'menu/closeNotificationCenter',
      setGlobalModal: 'ui/setGlobalModal'
    }),

    async handleNotificationClick(notification) {
      const notificationForMyVisitors =
        notification.type === 'VIEW_SUMMARY' ||
        notification.type === 'VIEW_SUMMARY_TRIAL';
      if (notificationForMyVisitors && this.primaryPropositionIsLimited) {
        this.closeNotificationCenter();

        this.setGlobalModal('MyVisitorsLocked');

        events.emitEvent(events.eventTypes.SEE, 'Paywall', {
          trigger: 'my-visitors-locked',
          src: 'notifications'
        });
      } else {
        // Use this code until setSwapType() + filterType url param logic is fixed
        try {
          await this.markAsRead(notification.id);
        } catch (error) {
          // The read mark is a side effect: when it fails the click must
          // still open the notification. The failure goes to Sentry.
          Sentry.captureException(error);
        }
        window.location.href = notification.webUrl;

        // this.markAsRead(notification.id);

        // if (notification.webUrl) {
        //   this.$router.replace(notification.webUrl).catch(error => {
        //     console.error(error);
        //   });
        // }

        // this.closeNotificationCenter();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.notification-center-container {
  background-color: #fff;
  padding-top: 25px;
  padding-bottom: 20px;
  overflow-y: auto;
  user-select: none;
  z-index: 2001;

  @media ($mobile) {
    position: fixed;
    width: 100vw;
    top: 60px;
    bottom: calc(64px + env(safe-area-inset-bottom));
    right: 0;
  }

  @media ($desktop) {
    position: absolute;
    right: -12px;
    top: 55px;
    height: 80vh;
    width: 375px;
    border-radius: 10px;
    box-shadow: 0px 0px 35px rgba(0, 0, 0, 0.15);
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.notification-center-heading {
  display: inline;
  font-size: 28px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 28px;
  margin-left: 28px;
}

.no-notifications {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  margin-top: 100px;
}

.no-notifications-icon {
  margin-bottom: 16px;
}

.no-notifications-heading {
  font-size: 20px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 12px;
}

.no-notifications-text {
  color: var(--Text-Secondary);
  font-weight: 400;
  line-height: 20px;
  margin-top: 0;
}

.section-header {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  margin-left: 28px;
}

@keyframes notification-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.notification-skeleton {
  padding-top: 16px;
  animation: notification-fade-in 0.15s ease 0.3s both;
}

@keyframes notification-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.notification-skeleton-item {
  display: flex;
  padding: 10px 15px 10px 28px;
  margin-bottom: 10px;
}

.notification-skeleton-icon {
  flex: 0 0 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(90deg, #edeef0 25%, #f8f8f9 50%, #edeef0 75%);
  background-size: 200% 100%;
  animation: notification-shimmer 1.5s infinite;
}

.notification-skeleton-body {
  margin-left: 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notification-skeleton-line {
  border-radius: 4px;
  background: linear-gradient(90deg, #edeef0 25%, #f8f8f9 50%, #edeef0 75%);
  background-size: 200% 100%;
  animation: notification-shimmer 1.5s infinite;

  &--message {
    height: 14px;
    width: 90%;
  }

  &--message-short {
    height: 14px;
    width: 60%;
  }

  &--timestamp {
    height: 12px;
    width: 30%;
    margin-top: 2px;
  }
}
</style>
